import React, { useId, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Crown } from 'lucide-react';
import type { BlastRadiusScoreDocument } from '../../api-utils/blast';
import type { EntityRiskScoreDocument } from '../../api-utils/entity_risk';
import type { SecurityEventItem } from '../../api-utils/security_events';
import type { ThreatEventItem } from '../../api-utils/threats';
import type { ForceGraphNode } from './correlationGraph';
import { ebrScoreOf, hasEriData, titleCase } from './dossierModel';
import { formatDate, scoreCls, toScoreNumber } from './eriUtils';

interface NodeCardProps {
  node: ForceGraphNode;
  eri?: EntityRiskScoreDocument;
  ebr?: BlastRadiusScoreDocument;
  /** The incident or campaign the node stands for (or belongs to). */
  scopedThreatEvent?: ThreatEventItem | null;
  scopedSecurityEvents?: SecurityEventItem[];
  /** How many actions the campaign joins; set only when the node is shown as a campaign. */
  campaignMemberCount?: number;
  onDetails: () => void;
  onOpenIncident: (id: string) => void;
  /** Absent when the entity has no dossier to open. */
  onOpenEntity?: (name: string) => void;
  /** Makes this entity the scope of the graph. Absent when it already is, or for a threat node. */
  onFocusEntity?: () => void;
  /** The crown-jewel assets the incident or campaign contains, named; empty when there is none. Absent for an entity. */
  crownJewels?: string[];
}

/** Whether an incident or a campaign contains a crown-jewel asset (client notes 11/2), in words and with an icon. */
export const CrownJewelLine: React.FC<{ names: string[] }> = ({ names }) => {
  const has = names.length > 0;
  return (
    <div className={`c3-crown${has ? ' is-yes' : ''}`}>
      <Crown size={14} strokeWidth={1.5} aria-hidden />
      <span>{has ? `Crown jewel involved: ${names.join(', ')}` : 'No crown jewel involved'}</span>
    </div>
  );
};

interface Fact {
  label: string;
  value: React.ReactNode;
}

/**
 * The selected node on the graph's canvas: what it is, three facts, and what to do with it. The full dossier (history,
 * attack path, risk breakdown, blast radius) opens from Details; `NodeDossier` is that one, unchanged.
 */
const NodeCard: React.FC<NodeCardProps> = ({
  node,
  eri,
  ebr,
  scopedThreatEvent,
  scopedSecurityEvents = [],
  campaignMemberCount,
  onDetails,
  onOpenIncident,
  onOpenEntity,
  onFocusEntity,
  crownJewels,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const detailsRef = useRef<HTMLButtonElement>(null);
  const isCampaign =
    String(node.universalType || '').toLowerCase() === 'campaign' || (campaignMemberCount ?? 0) > 0;
  const isEntity = node.layer !== 'threat';
  const providerRef = [node.provider, node.externalId].filter(Boolean).join(':');
  // The domain and the type are often the same word ("Cloud · Cloud"): say it once.
  const typeLine = [titleCase(node.layer || ''), titleCase(node.universalDomain), titleCase(node.universalType)]
    .filter((part, index, parts) => Boolean(part) && part !== parts[index - 1])
    .join(' · ');

  const facts: Fact[] = [];
  if (isEntity) {
    const eriScore = hasEriData(eri) ? toScoreNumber(eri.score) : null;
    const ebrScore = ebrScoreOf(ebr);
    if (eriScore != null)
      facts.push({ label: 'Risk', value: <span className={`c3-node-card-score ${scoreCls(eriScore)}`}>{Math.round(eriScore)}</span> });
    if (ebrScore != null)
      facts.push({ label: 'EBR', value: <span className={`c3-node-card-score ${scoreCls(ebrScore)}`}>{Math.round(ebrScore)}</span> });
    facts.push({ label: 'Edges', value: node.linkCount });
  } else {
    const firstSeen = scopedThreatEvent?.first_event_time || scopedThreatEvent?.created_at;
    if (isCampaign) {
      facts.push({ label: 'Incidents', value: campaignMemberCount ?? 0 });
    } else if (scopedThreatEvent?.detection_classification) {
      facts.push({ label: 'Classified as', value: scopedThreatEvent.detection_classification });
    }
    if (firstSeen) facts.push({ label: 'First seen', value: formatDate(firstSeen) });
    // Only a campaign hands the card its events (an incident's are in the Attack Path rail and the dossier).
    if (isCampaign && scopedSecurityEvents.length > 0) facts.push({ label: 'Events', value: scopedSecurityEvents.length });
  }

  // Not the node's id: it may hold spaces or colons, which aria-labelledby cannot take.
  const headingId = useId();

  return (
    <section className="c3-node-card" aria-labelledby={headingId}>
      <div className="c3-node-card-head">
        <div className="c3-node-card-kicker">
          <span>{titleCase(node.universalDomain || node.layer || 'Entity')}</span>
          {providerRef && !collapsed ? (
            <span className="c3-node-card-ref" title={providerRef}>
              {providerRef}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          className="c3-node-card-fold"
          aria-expanded={!collapsed}
          aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${node.label}`}
          onClick={() => setCollapsed((prev) => !prev)}
        >
          {collapsed ? <ChevronUp size={16} strokeWidth={1.5} aria-hidden /> : <ChevronDown size={16} strokeWidth={1.5} aria-hidden />}
        </button>
      </div>
      <h3 id={headingId} className="c3-node-card-title">
        {node.label}
      </h3>
      {crownJewels ? <CrownJewelLine names={crownJewels} /> : null}
      {collapsed ? null : (
        <>
          {typeLine ? <div className="c3-node-card-type">{typeLine}</div> : null}
          {facts.length > 0 ? (
            <dl className="c3-node-card-facts">
              {facts.map((fact) => (
                <div key={fact.label}>
                  <dt>{fact.label}</dt>
                  <dd>{fact.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}
          <div className="c3-node-card-actions">
            {scopedThreatEvent && !isCampaign ? (
              <button type="button" className="c3-pill" onClick={() => onOpenIncident(scopedThreatEvent._id)}>
                Remediate
              </button>
            ) : isEntity && onOpenEntity ? (
              <button type="button" className="c3-pill" onClick={() => onOpenEntity(node.label)}>
                Open entity
              </button>
            ) : null}
            {onFocusEntity ? (
              <button
                type="button"
                className="c3-pill"
                onClick={() => {
                  onFocusEntity();
                  // The button is gone once the entity is the scope: keep the keyboard on the card.
                  detailsRef.current?.focus();
                }}
                title="Show only this entity and what is around it"
              >
                Focus here
              </button>
            ) : null}
            <button ref={detailsRef} type="button" className="c3-pill" onClick={onDetails}>
              Details
            </button>
          </div>
        </>
      )}
    </section>
  );
};

export default NodeCard;
